import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardActionArea from '@mui/material/CardActionArea'
import CardContent from '@mui/material/CardContent'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { AircraftDentViewer, dentAssetUrl, dentAttachmentUrl, dentMarkers, formatDate } from '@jupi/ui'
import { useAircraftDents } from '../api/queries'

// Dents and buckles on the aircraft, as markers on its type's 3D model or
// silhouette. Nothing at all unless there is a model or silhouette AND at
// least one recorded dent.
export default function AircraftDentsCard({ registration }: { registration: string }) {
  const { t } = useTranslation()
  const { data } = useAircraftDents(registration)
  const [selected, setSelected] = useState<number | null>(null)

  if (!data?.view || !data.aircraftType || data.dents.length === 0) return null
  const assetUrl = dentAssetUrl(data.view === '3d' ? 'models' : 'silhouettes', data.aircraftType)

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('aircraftDent.title')}
        </Typography>
        <Stack spacing={2}>
          <AircraftDentViewer view={data.view} assetUrl={assetUrl} dents={dentMarkers(data.dents, { dateOnly: true })} selectedId={selected} onSelect={setSelected} />
          <Stack spacing={1}>
            {data.dents.map((dent, index) => (
              <Card key={dent.dentId} variant="outlined" sx={{ borderColor: dent.dentId === selected ? 'warning.main' : undefined }}>
                <CardActionArea onClick={() => setSelected(dent.dentId)} sx={{ p: 1.5 }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
                    <Typography variant="subtitle2">#{index + 1}</Typography>
                    <Typography variant="body2" sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
                      {dent.note}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(dent.createdAt)}
                    </Typography>
                  </Stack>
                </CardActionArea>
                {dent.attachments.length > 0 ? (
                  <Box sx={{ px: 1.5, pb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {dent.attachments.map((a) =>
                      a.contentType.startsWith('image/') ? (
                        <Link key={a.attachmentId} href={dentAttachmentUrl(a.attachmentId)} target="_blank" rel="noopener">
                          <Box
                            component="img"
                            src={dentAttachmentUrl(a.attachmentId)}
                            alt={a.filename}
                            sx={{ height: 72, borderRadius: 1, display: 'block' }}
                          />
                        </Link>
                      ) : (
                        <Link key={a.attachmentId} href={dentAttachmentUrl(a.attachmentId)} variant="body2">
                          <Icon icon="mdi:file-pdf-box" /> {a.filename}
                        </Link>
                      ),
                    )}
                  </Box>
                ) : null}
              </Card>
            ))}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  )
}
